/**
 * The lofi station's playlist (lane C0-LOFI): the calm tracks of music/tracks/lofi.ts one after another, each played
 * through once (intro and one pass of its loop, about two minutes) and then crossfaded into the next by C0's music
 * director. Which track was playing is remembered in this browser (localStorage, guarded), so the station picks up
 * where the viewer left it.
 */
import { music } from '../music';
import { resolveTrack } from '../music/library';
import { loopSeconds } from '../music/compile';
import { INSTRUMENTS } from '../music/types';
import { LOFI_TRACKS } from '../music/tracks/lofi';
import { getSoundMode, setSoundMode } from './soundMode';

const KEY = 'c0lofi.track';
const PAUSED = 'c0lofi.paused';
const SHUFFLE = 'c0lofi.shuffle';
const flag = (k: string): boolean => {
  try {
    return localStorage.getItem(k) === '1';
  } catch {
    return false;
  }
};
const keep = (k: string, v: string): void => {
  try {
    localStorage.setItem(k, v);
  } catch {
    // not remembered
  }
};

const load = (): number => {
  try {
    const i = Number(localStorage.getItem(KEY));
    return Number.isInteger(i) && i >= 0 && i < LOFI_TRACKS.length ? i : 0;
  } catch {
    return 0;
  }
};

/** A sound bed the front's ambience adds under a track (rain on the roof for the rain track). */
export const TRACK_BED: Partial<
  Record<(typeof LOFI_TRACKS)[number], Partial<Record<'rain' | 'wind' | 'water' | 'crickets', number>>>
> = {
  lofiRain: { rain: 0.7 },
  lofiFalls: { water: 0.35 },
  lofiLantern: { crickets: 0.35 },
  lofiPeak: { wind: 0.4 },
};

const LENGTH = new Map<string, number>();

class Station {
  private i = load();
  private on = false;
  /** Seconds of the track heard so far (paused time does not count). */
  private heard = 0;
  private timer = 0;
  /** Held silent by the viewer (the player's pause); remembered. */
  paused = flag(PAUSED);
  /** The next track is picked at random; remembered. */
  shuffle = flag(SHUFFLE);
  /** Listeners for a track change (the listen panel's title). */
  private subs = new Set<() => void>();

  get playing(): boolean {
    return this.on;
  }

  get id(): (typeof LOFI_TRACKS)[number] {
    return LOFI_TRACKS[this.i]!;
  }

  /** Thai title of the track now. */
  get title(): string {
    return resolveTrack(this.id)?.title ?? '';
  }

  /** The band in Thai: the lead and the answering voice ("กู่เจิง · ขลุ่ยเซียว"). */
  get band(): string {
    const def = resolveTrack(this.id);
    if (!def) return '';
    const names = [
      ...new Set(
        Object.values(def.parts)
          .filter((p) => p.inst !== 'perc' && p.inst !== 'pad')
          .map((p) => INSTRUMENTS[p.inst].thai),
      ),
    ];
    return names.join(' · ');
  }

  /** Seconds this track plays before the next comes in. */
  private length(): number {
    let n = LENGTH.get(this.id);
    if (n === undefined) {
      const def = resolveTrack(this.id);
      n = def ? Math.max(90, loopSeconds(def) + 8) : 120;
      LENGTH.set(this.id, n);
    }
    return n;
  }

  /** Starts (or keeps) the station playing. */
  play(): void {
    if (!this.on) {
      this.on = true;
      this.heard = 0;
      music.play(this.paused ? 'none' : this.id);
      this.emit();
    }
    if (!this.timer) this.timer = window.setInterval(() => this.tick(), 1000);
  }

  /** Stops advancing (another track took over); the music itself is the caller's. */
  pause(): void {
    this.on = false;
    window.clearInterval(this.timer);
    this.timer = 0;
  }

  next(step = 1): void {
    // Pressing the player in nature-only mode means the viewer wants music: switch to both.
    if (getSoundMode() === 'nature') setSoundMode('both');
    const n = LOFI_TRACKS.length;
    if (this.shuffle && step > 0) this.i = (this.i + 1 + Math.floor(Math.random() * (n - 1))) % n;
    else this.i = (this.i + step + n) % n;
    keep(KEY, String(this.i));
    this.heard = 0;
    if (this.on && !this.paused) music.play(this.id);
    this.emit();
  }

  /** Play / pause (the music fades out and back in; the scene's ambience goes on). */
  toggle(): void {
    if (getSoundMode() === 'nature') setSoundMode('both');
    this.paused = !this.paused;
    keep(PAUSED, this.paused ? '1' : '0');
    if (this.on) music.play(this.paused ? 'none' : this.id);
    this.emit();
  }

  toggleShuffle(): void {
    this.shuffle = !this.shuffle;
    keep(SHUFFLE, this.shuffle ? '1' : '0');
    this.emit();
  }

  onChange(f: () => void): () => void {
    this.subs.add(f);
    return () => this.subs.delete(f);
  }

  private emit(): void {
    for (const f of this.subs) f();
  }

  private tick(): void {
    if (!this.on || this.paused) return;
    this.heard += 1;
    if (this.heard >= this.length()) this.next();
  }
}

export const station = new Station();
